import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { SkilloLogo } from '@/components/SkilloLogo'
import {
  IconArrowUpRight,
  IconBriefcase,
  IconGlobe,
  IconMail,
  IconMapPin,
  IconPhone,
} from '@/components/Icons'
import { BrandMark } from '@/components/ToolLogo'
import { SOCIAL_ICONS } from '@/lib/toolIcons'
import { courses } from '@/lib/courses'
import { buildCounsellingWhatsAppUrl, siteConfig } from '@/lib/siteConfig'
import { getEnrollment } from '@/lib/intake'
import { useCurrentTime } from '@/lib/hydration'
import { CONTACT_PATH } from '@/lib/portal/paths'

const LINK_CLASS =
  'text-[var(--surface-muted)] no-underline transition-colors hover:text-[var(--text-strong)] focus:outline-none focus-visible:text-[var(--text-strong)] focus-visible:underline'

const HEADING_CLASS =
  'mb-4 font-code text-[11px] font-medium uppercase tracking-[0.22em] text-[var(--surface-muted)]'

function SocialLink({
  href,
  label,
  children,
}: {
  href: string
  label: string
  children: ReactNode
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="flex size-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-[var(--surface-fg)] no-underline transition-[border-color,background-color,color] duration-300 hover:border-[var(--color-brand-accent)]/50 hover:bg-[var(--color-brand-accent)]/10 hover:text-[var(--text-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)]"
    >
      {children}
    </a>
  )
}

export function SiteFooter() {
  const location = useLocation()
  const now = useCurrentTime()
  const enrollment = getEnrollment(now)
  const openCourses = courses
    .filter((course) => course.status === 'open')
    .sort((a, b) => a.priority - b.priority)

  const links = [
    { to: '/', label: 'Home' },
    { to: '/courses', label: 'All Courses' },
    { to: siteConfig.campaign.path, label: `${enrollment.batchMonth} Career Launch` },
    { to: '/free-career-orientation', label: 'Free Orientation' },
    { to: '/sylhet/freelancing-ai-web-development-courses', label: 'Courses in Sylhet' },
    { to: '/free-career-counselling', label: 'Free Career Counselling' },
    { to: '/student-projects', label: 'Student Projects' },
    { to: '/guides', label: 'Guides' },
    { to: '/about', label: 'About Skillo' },
    { to: CONTACT_PATH, label: 'Contact' },
  ]

  return (
    <footer className="relative px-3 pb-3 pt-16 sm:px-4 sm:pb-4">
      <div className="relative mx-auto max-w-[1240px] overflow-hidden rounded-[28px] border border-white/[0.08] bg-[linear-gradient(180deg,#0e1013,#08090b)]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--color-brand-accent)]/60 to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 left-1/2 h-80 w-[720px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(0,229,255,0.1),transparent_65%)]"
        />
        <div className="relative grid gap-10 p-6 sm:p-10 lg:grid-cols-[1.4fr_0.8fr_0.9fr_1fr] lg:gap-8">
          <div>
            <Link
              to="/"
              className="inline-block rounded-lg no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)]"
              aria-label="Skillo Academy home"
              onClick={() => {
                if (location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }
              }}
            >
              <SkilloLogo variant="academy" height={28} />
            </Link>
            <p className="mt-4 max-w-[440px] text-sm leading-relaxed text-[var(--surface-muted)]">
              {siteConfig.brand.entityStatement}
            </p>
            <p className="mt-3 font-bangla text-sm text-[var(--surface-muted)]">
              ব্যবহারিক AI, Web Development ও career skill শিক্ষা - সিলেট থেকেই।
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <SocialLink href={siteConfig.social.founderWebsite} label="Founder website">
                <IconGlobe size={16} aria-hidden="true" />
              </SocialLink>
              <SocialLink href={siteConfig.social.founderGithub} label="GitHub">
                <BrandMark icon={SOCIAL_ICONS.github} tone="mono" />
              </SocialLink>
              <SocialLink href={siteConfig.social.founderLinkedIn} label="LinkedIn">
                <IconBriefcase size={16} aria-hidden="true" />
              </SocialLink>
              <SocialLink href={siteConfig.social.founderYoutube} label="YouTube">
                <BrandMark icon={SOCIAL_ICONS.youtube} tone="mono" />
              </SocialLink>
              <SocialLink href={siteConfig.social.founderX} label="X">
                <BrandMark icon={SOCIAL_ICONS.x} tone="mono" size={15} />
              </SocialLink>
            </div>
          </div>
          <div>
            <h2 className={HEADING_CLASS}>Open programs</h2>
            <ul className="grid list-none gap-2.5 p-0 text-sm">
              {openCourses.map((course) => (
                <li key={course.slug}>
                  <Link to={`/courses/${course.slug}`} className={LINK_CLASS}>
                    {course.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className={HEADING_CLASS}>Quick Links</h2>
            <ul className="grid list-none gap-2.5 p-0 text-sm">
              {links.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={LINK_CLASS}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className={HEADING_CLASS}>Contact</h2>
            <ul className="grid list-none gap-3 p-0 text-sm text-[var(--surface-muted)]">
              <li className="flex items-center gap-2.5">
                <IconMapPin
                  size={14}
                  aria-hidden="true"
                  className="shrink-0 text-[var(--color-brand-accent-readable)]"
                />
                {siteConfig.contact.location}
              </li>
              <li className="flex items-center gap-2.5">
                <IconPhone
                  size={14}
                  aria-hidden="true"
                  className="shrink-0 text-[var(--color-brand-accent-readable)]"
                />
                <a href={buildCounsellingWhatsAppUrl(now)} className={LINK_CLASS}>
                  {siteConfig.contact.phoneDisplay}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <IconMail
                  size={14}
                  aria-hidden="true"
                  className="shrink-0 text-[var(--color-brand-accent-readable)]"
                />
                <a href={`mailto:${siteConfig.contact.email}`} className={LINK_CLASS}>
                  {siteConfig.contact.email}
                </a>
              </li>
              <li>
                <Link
                  to={CONTACT_PATH}
                  className="group mt-1 inline-flex items-center gap-1.5 rounded-full border border-[var(--color-brand-accent)]/30 bg-[var(--color-brand-accent)]/[0.07] px-4 py-2 font-semibold text-[var(--color-brand-accent-readable)] no-underline transition-colors hover:bg-[var(--color-brand-accent)]/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)]"
                >
                  Send an enquiry
                  <IconArrowUpRight
                    size={13}
                    aria-hidden="true"
                    className="transition-transform group-hover:-translate-y-px group-hover:translate-x-px"
                  />
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none select-none px-6 font-display text-[clamp(72px,17vw,220px)] font-bold leading-[0.78] tracking-[-0.06em] text-white/[0.025] sm:px-10"
        >
          Skillo
        </div>
        <div className="relative flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] px-6 py-5 text-xs text-[var(--surface-muted)] sm:px-10">
          <span>
            &copy; {now.getFullYear()} {siteConfig.brand.academyName}. All rights reserved.
          </span>
          <span className="text-hand text-base text-[var(--marker)]">
            Built and taught in Sylhet
          </span>
        </div>
      </div>
    </footer>
  )
}
